import {
  AfterViewInit,
  ContentChild,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  Renderer2
} from '@angular/core';

import { DndConfig } from './dnd-config';
import { DndPlaceholderDirective } from './dnd-placeholder.directive';
import { DataTransferEffect, DndDropEvent, EventTypes } from './models';
import { StateService } from './state.service';
import {
  DndEvent,
  getDirectChildElement,
  indexOfElement,
  shouldPositionPlaceholderBeforeElement} from './utils';

@Directive({
  selector: '[pcsDroppable]'
})
export class DroppableDirective implements AfterViewInit, OnDestroy {
  @Input()
  set isDisabled(value: boolean) {
    this.disabled = value;

    if (this.disabled) {
      this.renderer.addClass(this.container, this.config.droppableDisabledClass);
    } else {
      this.renderer.removeClass(this.container, this.config.droppableDisabledClass);
    }
  }

  @Output() readonly dndDrop: EventEmitter<DndDropEvent> = new EventEmitter<DndDropEvent>();

  @ContentChild(DndPlaceholderDirective) private readonly dndPlaceholderRef?: DndPlaceholderDirective;

  @HostBinding('class')
  get droppableClass() {
    return this.config.droppableClass;
  }

  private placeholder: Element = null;
  private disabled = false;

  private readonly dragEnterEventHandler: (event: DragEvent) => void = (event: DragEvent) => this.onDragEnter(event);
  private readonly dragOverEventHandler: (event: DragEvent) => void = (event: DragEvent) => this.onDragOver(event);
  private readonly dragLeaveEventHandler: (event: DragEvent) => void = (event: DragEvent) => this.onDragLeave(event);

  get container() {
    return this.elementRef && this.elementRef.nativeElement;
  }

  constructor(
    private ngZone: NgZone,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private stateService: StateService,
    @Inject(DndConfig) private config
  ) {
  }

  ngAfterViewInit(): void {
    this.placeholder = this.getPlaceholder();

    if (this.placeholder) {
      this.placeholder.parentNode.removeChild(this.placeholder);
    }

    this.ngZone.runOutsideAngular(() => {
      this.container.addEventListener(EventTypes.DragEnter, this.dragEnterEventHandler);
      this.container.addEventListener(EventTypes.DragOver, this.dragOverEventHandler);
      this.container.addEventListener(EventTypes.DragLeave, this.dragLeaveEventHandler);
    });
  }

  ngOnDestroy(): void {
    this.container.removeEventListener(EventTypes.DragEnter, this.dragEnterEventHandler);
    this.container.removeEventListener(EventTypes.DragOver, this.dragOverEventHandler);
    this.container.removeEventListener(EventTypes.DragLeave, this.dragLeaveEventHandler);
  }

  onDragEnter(event: DndEvent) {
    event.preventDefault();
  }

  onDragOver(event: DragEvent) {
    if (!this.isDropAllowed()) {
      return;
    }

    this.updatePlaceholderPosition(event);

    event.preventDefault();
    this.stateService.setDropEffect(event, DataTransferEffect.Move);
    this.renderer.addClass(this.container, this.config.dragOverClass);
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    if (!this.isDropAllowed()) {
      return;
    }

    event.preventDefault();
    this.stateService.setDropEffect(event, DataTransferEffect.Move);

    const dropIndex = this.getPlaceholderIndex();

    this.dndDrop.emit({
      event,
      data: this.stateService.dragState.data,
      index: dropIndex,
      startIndex: this.stateService.dragState.startIndex
    });

    event.stopPropagation();
    this.cleanupDragOverState();
  }

  onDragLeave(event: DndEvent) {
    const newTarget = document.elementFromPoint(event.clientX, event.clientY);

    if (this.container.contains(newTarget)) {
      return;
    }

    this.cleanupDragOverState();
    this.stateService.setDropEffect(event, DataTransferEffect.None);
  }

  private getPlaceholder(): Element {
    return this.dndPlaceholderRef && this.dndPlaceholderRef.elementRef.nativeElement as Element;
  }

  private isDropAllowed(): boolean {
    return !this.disabled;
  }

  private updatePlaceholderPosition(event: DragEvent) {
    if (!this.placeholder) {
      return;
    }

    if (this.placeholder.parentNode !== this.container) {
      this.renderer.appendChild(this.container, this.placeholder);
    }

    const directChild = getDirectChildElement(this.container, event.target as Element);

    if (!directChild || directChild === this.placeholder) {
      return;
    }

    const positionPlaceholderBeforeDirectChild = shouldPositionPlaceholderBeforeElement(event, directChild);

    if (positionPlaceholderBeforeDirectChild) {
      if (directChild.previousSibling !== this.placeholder) {
        // insert before
        this.renderer.insertBefore(this.container, this.placeholder, directChild);
      }
    } else if (directChild.nextSibling !== this.placeholder) {
      // insert after
      this.renderer.insertBefore(this.container, this.placeholder, directChild.nextSibling);
    }
  }

  private getPlaceholderIndex(): number {
    return indexOfElement(this.container, this.placeholder);
  }

  private cleanupDragOverState() {
    this.renderer.removeClass(this.container, this.config.dragOverClass);

    if (this.placeholder && this.placeholder.parentNode) {
      this.placeholder.parentNode.removeChild(this.placeholder);
    }
  }
}
